import type { CSSProperties, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  useGetDonationsLedgerQuery,
  useListPaymentMirrorLedgerEntriesQuery,
} from "../../../state/domainRouterApi";
import {
  formatCurrencyThousands,
  SourceFaithfulChartCard,
  sourceFaithfulChartAxisTick,
  sourceFaithfulChartLegendStyle,
  sourceFaithfulPalette,
  sourceFaithfulChartTooltipStyle,
} from "../shared/SourceFaithfulRecharts";
import {
  loadTuitionDashboardSnapshot,
  type TuitionDashboardSnapshot,
} from "../../tuition/shared/tuitionData";
import { ACTIVE_TUITION_ACADEMIC_YEAR } from "../../tuition/shared/academicYear";
import {
  ModuleSectionNav,
  type ModuleSectionNavItem,
} from "../../../components/navigation/ModuleSectionNav";
import { YstRouteTransition } from "../../../components/motion/YstRouteTransition";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";

const ACADEMIC_YEAR = ACTIVE_TUITION_ACADEMIC_YEAR;

const FINANCE_SECTION_ITEMS: ModuleSectionNavItem[] = [
  { id: "overview", label: "Overview", to: "/school/finance", end: true },
  { id: "revenue", label: "Revenue", to: "/school/finance/revenue" },
  { id: "cash-flow", label: "Cash flow", to: "/school/finance/cash-flow" },
  {
    id: "reconciliation",
    label: "Reconciliation",
    to: "/school/finance/reconciliation",
  },
  { id: "journals", label: "Journals", to: "/school/finance/journals" },
  { id: "export", label: "Export", to: "/school/finance/export" },
];

type JsonRecord = Record<string, unknown>;

const normalizeText = (value: unknown): string => String(value ?? "").trim();

const normalizeOptionalText = (value: unknown): string | null => {
  const normalized = normalizeText(value);
  return normalized || null;
};

const toNumber = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return 0;
};

const asRecord = (value: unknown): JsonRecord =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as JsonRecord)
    : {};

const asArray = <T,>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

const formatCurrency = (cents: number): string =>
  `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const formatEventDate = (value: unknown): string => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return new Date(value * 1000).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  const normalized = normalizeOptionalText(value);
  if (!normalized) {
    return "--";
  }
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return normalized;
  }
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const buildMonthKeys = (count = 6): string[] => {
  const cursor = new Date();
  cursor.setDate(1);
  const months: string[] = [];
  for (let index = count - 1; index >= 0; index -= 1) {
    const date = new Date(cursor.getFullYear(), cursor.getMonth() - index, 1);
    months.push(date.toISOString().slice(0, 7));
  }
  return months;
};

const monthLabel = (monthKey: string): string =>
  new Date(`${monthKey}-01T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    timeZone: "UTC",
  });

const entryMonthKey = (value: unknown): string | null => {
  const numeric =
    typeof value === "number" && Number.isFinite(value)
      ? new Date(value * 1000)
      : new Date(String(value ?? ""));
  if (Number.isNaN(numeric.getTime())) {
    return null;
  }
  return numeric.toISOString().slice(0, 7);
};

const chargeToCents = (item: JsonRecord): number =>
  Math.round(toNumber(item.grossCents) + toNumber(item.feeRecoveryCents));

const isCollectionEntry = (entryType: string): boolean =>
  ["payment", "credit", "scholarship", "adjustment"].includes(entryType);

const readPath = (
  value: unknown,
  path: readonly (string | number)[],
): unknown => {
  let current: unknown = value;
  for (const segment of path) {
    if (typeof segment === "number") {
      current = Array.isArray(current) ? current[segment] : undefined;
      continue;
    }
    current = asRecord(current)[segment];
  }
  return current;
};

const resolveMetadataObjects = (payload: unknown): JsonRecord[] =>
  [
    readPath(payload, ["data", "object", "metadata"]),
    readPath(payload, ["data", "object", "payment_intent", "metadata"]),
    readPath(payload, ["data", "object", "subscription_details", "metadata"]),
    readPath(payload, ["data", "object", "lines", "data", 0, "metadata"]),
    readPath(payload, ["data", "object", "parent", "metadata"]),
  ]
    .map(asRecord)
    .filter((candidate) => Object.keys(candidate).length > 0);

const resolveMetadataValue = (
  payload: unknown,
  keys: readonly string[],
): string | null => {
  const candidates = resolveMetadataObjects(payload);
  for (const candidate of candidates) {
    for (const key of keys) {
      const value = normalizeOptionalText(candidate[key]);
      if (value) {
        return value;
      }
    }
  }
  return null;
};

const LoadingSkeleton = () => (
  <div
    className="source-faithful-shell font-body"
    style={{ "--ystnj-source-faithful-max-width": "1120px" } as CSSProperties}
  >
    <div className="source-faithful-container">
      <div className="h-16 animate-pulse rounded-xl bg-muted" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="h-28 animate-pulse rounded-xl bg-muted" />
        ))}
      </div>
      <div className="h-72 animate-pulse rounded-xl bg-muted" />
      <div className="h-80 animate-pulse rounded-xl bg-muted" />
    </div>
  </div>
);

const ErrorBanner = ({ message }: { message: string }) => (
  <SupportErrorMessage>
    {message}
  </SupportErrorMessage>
);

const WarningBanner = ({ messages }: { messages: string[] }) => (
  <div className="rounded-xl border border-amber-200 bg-amber-50 px-6 py-5 text-sm text-amber-800">
    <div className="space-y-1">
      {messages.map((message) => (
        <p key={message}>{message}</p>
      ))}
    </div>
  </div>
);

const Shell = ({
  title,
  children,
  testId,
  metrics,
  warnings,
}: {
  title: string;
  children: ReactNode;
  testId: string;
  metrics: Array<{ label: string; value: string; color: string }>;
  warnings?: string[];
}) => (
  <main
    data-testid={testId}
    data-extracted-from="source-faithful:school-financial-operations-v1.0"
    data-route-family="source-faithful"
    data-route-ready="true"
    className="source-faithful-shell font-body"
    style={{ "--ystnj-source-faithful-max-width": "1120px" } as CSSProperties}
  >
    <YstRouteTransition className="source-faithful-container">
      <header className="source-faithful-header">
        <div>
          <p className="yst-surface-eyebrow !text-text-secondary">
            Academic year {ACADEMIC_YEAR}
          </p>
          <h2 className="yst-surface-title">{title}</h2>
        </div>
        <select
          className="yst-control-input w-full max-w-[260px] px-3 py-2.5 text-sm"
          value={ACADEMIC_YEAR}
          disabled
          aria-label="Academic year"
        >
          <option>{ACADEMIC_YEAR}</option>
        </select>
      </header>

      <ModuleSectionNav
        items={FINANCE_SECTION_ITEMS}
        ariaLabel="Financial operations sections"
        desktopClassName="source-faithful-tabs"
        testIds={{
          root: "finance-section-nav",
          itemPrefix: "finance-section-nav-item",
          mobileTrigger: "finance-section-nav-mobile-trigger",
          mobileDialog: "finance-section-nav-mobile-dialog",
          mobileItemPrefix: "finance-section-nav-mobile-item",
        }}
      />

      <section className="source-faithful-kpiGrid">
        {metrics.map((metric) => (
          <article
            key={metric.label}
            className="source-faithful-panel source-faithful-kpiCard flex h-full flex-col justify-between p-5"
          >
            <div className="mb-4 flex items-center gap-2">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: metric.color }}
              />
              <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--text-secondary)]">
                {metric.label}
              </p>
            </div>
            <p className="source-faithful-kpiValue text-[clamp(1.875rem,4vw,2.5rem)] font-semibold leading-tight text-[var(--iron-grey)]">
              {metric.value}
            </p>
          </article>
        ))}
      </section>

      {warnings && warnings.length > 0 ? (
        <WarningBanner messages={warnings} />
      ) : null}

      {children}
    </YstRouteTransition>
  </main>
);

type FinanceSnapshot = {
  tuition: TuitionDashboardSnapshot | null;
  donationItems: JsonRecord[];
  paymentMirrorItems: JsonRecord[];
  metrics: Array<{ label: string; value: string; color: string }>;
  revenueTrend: Array<{
    month: string;
    tuition: number;
    donations: number;
    parnesHayom: number;
    other: number;
  }>;
  collectionTrend: Array<{ month: string; collected: number }>;
  topReceivables: Array<{
    familyName: string;
    balanceCents: number;
    statusLabel: string;
  }>;
  reconciliationRows: Array<{
    id: string;
    module: string;
    eventType: string;
    recordedAt: string;
    actor: string;
  }>;
  journalRows: Array<{
    id: string;
    source: string;
    description: string;
    amountCents: number;
    date: string;
  }>;
};

const buildFinanceSnapshot = (
  tuition: TuitionDashboardSnapshot | null,
  donationItems: JsonRecord[],
  paymentMirrorItems: JsonRecord[],
): FinanceSnapshot => {
  const generalDonationsCents = donationItems
    .filter((item) => normalizeText(item.campaignType) !== "parnes-hayom")
    .reduce((sum, item) => sum + chargeToCents(item), 0);
  const parnesHayomCents = donationItems
    .filter((item) => normalizeText(item.campaignType) === "parnes-hayom")
    .reduce((sum, item) => sum + chargeToCents(item), 0);
  const tuitionCollectedCents = tuition?.totalCollectedCents ?? 0;
  const totalRevenueCents =
    tuitionCollectedCents + generalDonationsCents + parnesHayomCents;
  const outstandingCents = tuition?.outstandingCents ?? 0;

  const monthKeys = buildMonthKeys(6);
  const revenueBuckets = new Map(
    monthKeys.map((monthKey) => [
      monthKey,
      { tuition: 0, donations: 0, parnesHayom: 0, other: 0, collected: 0 },
    ]),
  );

  for (const family of tuition?.families ?? []) {
    for (const transaction of family.transactions) {
      const monthKey = entryMonthKey(
        transaction.observedAt ?? transaction.internalEffectiveDate,
      );
      const bucket = monthKey ? revenueBuckets.get(monthKey) : null;
      if (!bucket) {
        continue;
      }
      const entryType = normalizeText(transaction.entryType);
      const amount = Math.abs(transaction.amountCents);
      if (isCollectionEntry(entryType)) {
        bucket.tuition += amount;
        bucket.collected += amount;
      } else if (
        entryType === "tuition" ||
        entryType === "registration_fee" ||
        entryType === "security_fee" ||
        entryType === "late_fee"
      ) {
        bucket.other += amount;
      }
    }
  }

  for (const item of donationItems) {
    const monthKey = entryMonthKey(item.createdAt);
    const bucket = monthKey ? revenueBuckets.get(monthKey) : null;
    if (!bucket) {
      continue;
    }
    const amount = chargeToCents(item);
    if (normalizeText(item.campaignType) === "parnes-hayom") {
      bucket.parnesHayom += amount;
    } else {
      bucket.donations += amount;
    }
    bucket.collected += amount;
  }

  const revenueTrend = monthKeys.map((monthKey) => {
    const bucket = revenueBuckets.get(monthKey) ?? {
      tuition: 0,
      donations: 0,
      parnesHayom: 0,
      other: 0,
      collected: 0,
    };
    return {
      month: monthLabel(monthKey),
      tuition: Math.round(bucket.tuition / 100000),
      donations: Math.round(bucket.donations / 100000),
      parnesHayom: Math.round(bucket.parnesHayom / 100000),
      other: Math.round(bucket.other / 100000),
    };
  });

  const collectionTrend = monthKeys.map((monthKey) => {
    const bucket = revenueBuckets.get(monthKey) ?? {
      collected: 0,
      tuition: 0,
      donations: 0,
      parnesHayom: 0,
      other: 0,
    };
    return {
      month: monthLabel(monthKey),
      collected: Math.round(bucket.collected / 100000),
    };
  });

  return {
    tuition,
    donationItems,
    paymentMirrorItems,
    metrics: [
      {
        label: "Total revenue",
        value: formatCurrency(totalRevenueCents),
        color: "var(--iron-grey)",
      },
      {
        label: "Tuition",
        value: formatCurrency(tuitionCollectedCents),
        color: "var(--primary)",
      },
      {
        label: "Donations",
        value: formatCurrency(generalDonationsCents),
        color: "var(--coral)",
      },
      {
        label: "Parnes hayom",
        value: formatCurrency(parnesHayomCents),
        color: "var(--jungle)",
      },
      {
        label: "Outstanding",
        value: formatCurrency(outstandingCents),
        color: "var(--text-tertiary)",
      },
    ],
    revenueTrend,
    collectionTrend,
    topReceivables: [...(tuition?.families ?? [])]
      .sort((left, right) => right.netBalanceCents - left.netBalanceCents)
      .slice(0, 6)
      .map((family) => ({
        familyName: family.familyName,
        balanceCents: family.netBalanceCents,
        statusLabel: family.statusLabel,
      })),
    reconciliationRows: paymentMirrorItems.slice(0, 8).map((entry, index) => ({
      id: String(entry.id ?? `event-${index}`),
      module: normalizeText(entry.moduleTarget) || "unknown",
      eventType: normalizeText(entry.eventType) || "event",
      recordedAt: formatEventDate(entry.recordedAt),
      actor:
        resolveMetadataValue(entry.payload, ["actorId", "actor_id"]) ??
        "system",
    })),
    journalRows: [
      ...donationItems.slice(0, 4).map((item) => ({
        id: String(item.id ?? `donation-${Math.random()}`),
        source: normalizeText(item.campaignName) || "Donation",
        description:
          normalizeText(item.honoreeName) ||
          normalizeText(item.donorFirstName) ||
          "Donation ledger entry",
        amountCents: chargeToCents(item),
        date: formatEventDate(item.createdAt),
      })),
      ...(tuition?.families ?? []).flatMap((family) =>
        family.transactions.slice(0, 1).map((transaction) => ({
          id: String(transaction.id ?? `${family.familyId}-txn`),
          source: family.familyName,
          description:
            normalizeText(transaction.description) ||
            normalizeText(transaction.entryType) ||
            "Family ledger entry",
          amountCents: transaction.amountCents,
          date: formatEventDate(
            transaction.observedAt ?? transaction.internalEffectiveDate,
          ),
        })),
      ),
    ].slice(0, 8),
  };
};

const RevenueChart = ({ data }: { data: FinanceSnapshot["revenueTrend"] }) => (
  <SourceFaithfulChartCard
    title="Collected revenue by source"
    subtitle="Tuition collections are composed from persisted family account transactions; donations are composed from the donations ledger."
    chartAriaLabel="Monthly collected revenue by tuition, donations, parnes hayom, and other finance entries for the last six months."
    chartHeightPx={340}
    testId="finance-monthly-revenue-chart"
  >
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        accessibilityLayer
        data={data}
        margin={{ top: 16, right: 12, left: -6, bottom: 0 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          vertical={false}
          stroke={sourceFaithfulPalette.border}
        />
        <XAxis
          dataKey="month"
          axisLine={false}
          tickLine={false}
          tick={sourceFaithfulChartAxisTick}
          dy={10}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={sourceFaithfulChartAxisTick}
          tickFormatter={formatCurrencyThousands}
          width={52}
        />
        <Tooltip
          cursor={{ fill: sourceFaithfulPalette.muted }}
          contentStyle={sourceFaithfulChartTooltipStyle}
          formatter={(value: number | string, name: string | number) => [
            formatCurrencyThousands(value),
            String(name),
          ]}
        />
        <Legend
          wrapperStyle={sourceFaithfulChartLegendStyle}
          iconType="circle"
        />
        <Bar
          dataKey="tuition"
          name="Tuition"
          stackId="revenue"
          fill={sourceFaithfulPalette.primary}
        />
        <Bar
          dataKey="donations"
          name="Donations"
          stackId="revenue"
          fill={sourceFaithfulPalette.coral}
        />
        <Bar
          dataKey="parnesHayom"
          name="Parnes HaYom"
          stackId="revenue"
          fill={sourceFaithfulPalette.jungle}
        />
        <Bar
          dataKey="other"
          name="Other"
          stackId="revenue"
          fill={sourceFaithfulPalette.textTertiary}
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  </SourceFaithfulChartCard>
);

const OverviewPanel = ({ finance }: { finance: FinanceSnapshot }) => {
  const latestCollection =
    finance.collectionTrend.length > 0
      ? (finance.collectionTrend[finance.collectionTrend.length - 1]
          ?.collected ?? 0)
      : 0;

  return (
    <div className="source-faithful-splitGrid">
      <article className="source-faithful-panel p-5">
        <h3 className="mb-4 text-[16px] font-semibold text-[var(--iron-grey)]">
          Outstanding receivables
        </h3>
        <p className="text-[32px] font-semibold leading-none text-[var(--iron-grey)]">
          {formatCurrency(finance.tuition?.outstandingCents ?? 0)}
        </p>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          Family tuition balances still open
        </p>
        <div className="mt-4 space-y-3">
          {finance.topReceivables.slice(0, 3).map((row) => (
            <div
              key={row.familyName}
              className="flex items-center justify-between rounded-[10px] border border-[var(--border)] bg-white px-4 py-3 text-sm"
            >
              <span className="font-medium text-[var(--iron-grey)]">
                {row.familyName}
              </span>
              <span className="text-[var(--text-secondary)]">
                {formatCurrency(row.balanceCents)} · {row.statusLabel}
              </span>
            </div>
          ))}
        </div>
      </article>
      <article className="source-faithful-panel p-5">
        <h3 className="mb-4 text-[16px] font-semibold text-[var(--iron-grey)]">
          Collected this period
        </h3>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[32px] font-semibold leading-none text-[var(--iron-grey)]">
              {formatCurrency(latestCollection * 100000)}
            </p>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Latest monthly intake
            </p>
          </div>
          <div
            className="h-16 w-[180px]"
            role="img"
            aria-label="Latest monthly collections trend for the last six months."
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                accessibilityLayer
                data={finance.collectionTrend}
                margin={{ top: 4, right: 4, left: 4, bottom: 4 }}
              >
                <Tooltip
                  contentStyle={sourceFaithfulChartTooltipStyle}
                  formatter={(value: number | string) => [
                    formatCurrencyThousands(value),
                    "Collected",
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="collected"
                  stroke={sourceFaithfulPalette.aquamarine}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 4, fill: sourceFaithfulPalette.aquamarine }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </article>
    </div>
  );
};

const DataTable = ({
  title,
  rows,
  headers,
}: {
  title: string;
  rows: string[][];
  headers: string[];
}) => (
  <section className="source-faithful-panel overflow-hidden">
    <div className="border-b border-[var(--border)] px-6 py-4">
      <h2 className="text-[16px] font-semibold text-[var(--iron-grey)]">
        {title}
      </h2>
    </div>
    <div className="source-faithful-tableScroll">
      <table className="min-w-[720px] divide-y divide-[var(--border)] text-sm">
        <thead className="bg-muted">
          <tr>
            {headers.map((header) => (
              <th
                key={header}
                className="px-6 py-3 text-left text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--text-secondary)]"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--border)]">
          {rows.map((row) => (
            <tr key={row.join("-")}>
              {row.map((cell) => (
                <td key={cell} className="px-6 py-4 text-[var(--iron-grey)]">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

const ExportPane = () => (
  <div className="source-faithful-kpiGrid">
    {[
      ["Tuition management", "Open live tuition balances", "/school/tuition"],
      [
        "Donations ledger",
        "Review campaign receipts",
        "/school/donations/ledger",
      ],
      [
        "Payments control plane",
        "Check Stripe routing status",
        "/school/settings",
      ],
    ].map(([title, subtitle, href]) => (
      <article key={title} className="source-faithful-panel p-5">
        <p className="text-[16px] font-semibold text-[var(--iron-grey)]">
          {title}
        </p>
        <p className="mt-2 text-sm text-[var(--text-secondary)]">{subtitle}</p>
        <a
          href={href}
          className="mt-4 inline-flex rounded-md border border-[var(--border)] px-3 py-2 text-sm font-medium text-[var(--iron-grey)]"
        >
          Open
        </a>
      </article>
    ))}
  </div>
);

function FinancePages({
  finance,
  warnings,
}: {
  finance: FinanceSnapshot;
  warnings: string[];
}) {
  return (
    <Routes>
      <Route
        index
        element={
          <Shell
            title="Financial operations"
            testId="finance-source-faithful-root"
            metrics={finance.metrics}
            warnings={warnings}
          >
            <RevenueChart data={finance.revenueTrend} />
            <OverviewPanel finance={finance} />
          </Shell>
        }
      />
      <Route
        path="revenue"
        element={
          <Shell
            title="Revenue mix"
            testId="finance-source-faithful-revenue"
            metrics={finance.metrics}
            warnings={warnings}
          >
            <RevenueChart data={finance.revenueTrend} />
          </Shell>
        }
      />
      <Route
        path="cash-flow"
        element={
          <Shell
            title="Cash flow"
            testId="finance-source-faithful-cash-flow"
            metrics={finance.metrics}
            warnings={warnings}
          >
            <DataTable
              title="Current receivables"
              headers={["Family", "Balance", "Status"]}
              rows={finance.topReceivables.map((row) => [
                row.familyName,
                formatCurrency(row.balanceCents),
                row.statusLabel,
              ])}
            />
          </Shell>
        }
      />
      <Route
        path="reconciliation"
        element={
          <Shell
            title="Reconciliation"
            testId="finance-source-faithful-reconciliation"
            metrics={finance.metrics}
            warnings={warnings}
          >
            {finance.reconciliationRows.length > 0 ? (
              <DataTable
                title="Recent payment mirror events"
                headers={["Module", "Event", "Recorded", "Actor"]}
                rows={finance.reconciliationRows.map((row) => [
                  row.module,
                  row.eventType,
                  row.recordedAt,
                  row.actor,
                ])}
              />
            ) : (
              <ErrorBanner message="Payment mirror reconciliation data is temporarily unavailable. Core finance views remain available while the mirror feed recovers." />
            )}
          </Shell>
        }
      />
      <Route
        path="journals"
        element={
          <Shell
            title="Journal"
            testId="finance-source-faithful-journals"
            metrics={finance.metrics}
            warnings={warnings}
          >
            <DataTable
              title="Recent live ledger activity"
              headers={["Source", "Description", "Amount", "Date"]}
              rows={finance.journalRows.map((row) => [
                row.source,
                row.description,
                formatCurrency(row.amountCents),
                row.date,
              ])}
            />
          </Shell>
        }
      />
      <Route
        path="export"
        element={
          <Shell
            title="Export"
            testId="finance-source-faithful-export"
            metrics={finance.metrics}
            warnings={warnings}
          >
            <ExportPane />
          </Shell>
        }
      />
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
}

export function FinancialOpsSourceFaithfulRoutes() {
  const [tuitionSnapshot, setTuitionSnapshot] =
    useState<TuitionDashboardSnapshot | null>(null);
  const [tuitionLoading, setTuitionLoading] = useState(true);
  const [tuitionError, setTuitionError] = useState<string | null>(null);
  const donationsQuery = useGetDonationsLedgerQuery({ limit: 100 });
  const paymentMirrorQuery = useListPaymentMirrorLedgerEntriesQuery({
    limit: 100,
  });

  useEffect(() => {
    let active = true;
    setTuitionLoading(true);
    setTuitionError(null);
    void loadTuitionDashboardSnapshot(ACADEMIC_YEAR, { limit: 24 })
      .then((snapshot) => {
        if (!active) {
          return;
        }
        setTuitionSnapshot(snapshot);
      })
      .catch((error) => {
        if (!active) {
          return;
        }
        setTuitionError(
          error instanceof Error
            ? error.message
            : "Failed to load tuition finance data.",
        );
      })
      .finally(() => {
        if (active) {
          setTuitionLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  if (
    tuitionLoading ||
    donationsQuery.isLoading ||
    paymentMirrorQuery.isLoading
  ) {
    return <LoadingSkeleton />;
  }

  if (tuitionError) {
    return (
      <div
        className="source-faithful-shell"
        style={
          { "--ystnj-source-faithful-max-width": "1120px" } as CSSProperties
        }
      >
        <div className="source-faithful-container">
          <ErrorBanner message={tuitionError} />
        </div>
      </div>
    );
  }
  const warnings = [
    donationsQuery.isError
      ? "The donations ledger is temporarily unavailable. Donation revenue metrics are showing the last recoverable baseline."
      : null,
    paymentMirrorQuery.isError
      ? "The payments mirror ledger is temporarily unavailable. Reconciliation detail is degraded, but the finance module remains available."
      : null,
  ].filter((value): value is string => Boolean(value));

  const donationItems = donationsQuery.isError
    ? []
    : asArray<JsonRecord>(asRecord(donationsQuery.data).items);
  const paymentMirrorItems = paymentMirrorQuery.isError
    ? []
    : asArray<JsonRecord>(asRecord(paymentMirrorQuery.data).items);
  const finance = buildFinanceSnapshot(
    tuitionSnapshot,
    donationItems,
    paymentMirrorItems,
  );

  return <FinancePages finance={finance} warnings={warnings} />;
}
